import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FiTrendingUp,
  FiUsers,
  FiMessageSquare,
  FiAward,
  FiHash,
  FiArrowUp,
  FiPlus,
  FiBookmark,
  FiChevronRight,
} from "react-icons/fi";
import { FaComment, FaThumbsUp, FaReply } from "react-icons/fa";
import { useCommunityFeedSidebar } from "../../../hooks/useCommunityFeedSidebar";
import { useSavedThreadIds } from "../../../services/communities";
import "./SidebarWidgets.css";

/**
 * SidebarWidgets - Compact sidebar with real API data
 * Widgets: Trending Topics, Your Activity, Rising Communities, Hot Discussions
 */
const SidebarWidgets = ({ onCreateCommunity }) => {
  const navigate = useNavigate();

  // Fetch real data from API
  const {
    trendingTopics,
    userActivity,
    risingCommunities,
    hotDiscussions,
    loading,
  } = useCommunityFeedSidebar();

  // Fetch saved thread IDs for count
  const { data: savedData } = useSavedThreadIds();

  // Format count helper
  const formatCount = (count) => {
    if (!count) return "0";
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return count.toString();
  };

  // Format time ago
  const formatTimeAgo = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 1) return "Just now";
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    return date.toLocaleDateString();
  };

  // Loading skeleton
  const WidgetSkeleton = () => (
    <div className="chub-widget-skeleton">
      <div className="skeleton-line short"></div>
      <div className="skeleton-line"></div>
      <div className="skeleton-line medium"></div>
    </div>
  );

  return (
    <div className="chub-widgets">
      {/* Trending Topics Widget */}
      <motion.div
        className="chub-widget"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <div className="chub-widget-header">
          <FiTrendingUp size={16} className="chub-widget-icon trending" />
          <h3>Trending Topics</h3>
        </div>
        <div className="chub-widget-content">
          {loading ? (
            <WidgetSkeleton />
          ) : trendingTopics.length > 0 ? (
            <div className="chub-topics-list">
              {trendingTopics.slice(0, 5).map((topic, index) => (
                <Link
                  key={topic.tag || index}
                  to={`/communities?tag=${encodeURIComponent(topic.tag)}`}
                  className="chub-topic-item"
                >
                  <span className="chub-topic-rank">#{index + 1}</span>
                  <div className="chub-topic-info">
                    <span className="chub-topic-tag">
                      <FiHash size={12} />
                      {topic.tag}
                    </span>
                    <span className="chub-topic-count">
                      {formatCount(topic.count)} posts
                    </span>
                  </div>
                  {topic.growthRate > 0 && (
                    <span className="chub-topic-growth">
                      <FiArrowUp size={10} />
                      {topic.growthRate}%
                    </span>
                  )}
                </Link>
              ))}
            </div>
          ) : (
            <div className="chub-widget-empty">
              <FiTrendingUp size={24} />
              <span>No trending topics</span>
            </div>
          )}
        </div>
      </motion.div>

      {/* Your Activity Widget */}
      <motion.div
        className="chub-widget"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <div className="chub-widget-header">
          <FiAward size={16} className="chub-widget-icon activity" />
          <h3>Your Activity</h3>
        </div>
        <div className="chub-widget-content">
          {loading ? (
            <WidgetSkeleton />
          ) : userActivity ? (
            <div className="chub-activity-grid">
              <div className="chub-activity-item">
                <div className="chub-activity-icon communities">
                  <FiUsers size={14} />
                </div>
                <div className="chub-activity-info">
                  <span className="chub-activity-value">
                    {formatCount(userActivity.communitiesJoined)}
                  </span>
                  <span className="chub-activity-label">Communities</span>
                </div>
              </div>
              <div className="chub-activity-item">
                <div className="chub-activity-icon threads">
                  <FaComment size={12} />
                </div>
                <div className="chub-activity-info">
                  <span className="chub-activity-value">
                    {formatCount(userActivity.threadsCreated)}
                  </span>
                  <span className="chub-activity-label">Threads</span>
                </div>
              </div>
              <div className="chub-activity-item">
                <div className="chub-activity-icon upvotes">
                  <FaThumbsUp size={12} />
                </div>
                <div className="chub-activity-info">
                  <span className="chub-activity-value">
                    {formatCount(userActivity.upvotesReceived)}
                  </span>
                  <span className="chub-activity-label">Upvotes</span>
                </div>
              </div>
              <div className="chub-activity-item">
                <div className="chub-activity-icon replies">
                  <FaReply size={12} />
                </div>
                <div className="chub-activity-info">
                  <span className="chub-activity-value">
                    {formatCount(userActivity.repliesPosted)}
                  </span>
                  <span className="chub-activity-label">Replies</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="chub-widget-empty">
              <FiAward size={24} />
              <span>Join communities to see activity</span>
            </div>
          )}
        </div>
      </motion.div>

      {/* Rising Communities Widget */}
      <motion.div
        className="chub-widget"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <div className="chub-widget-header">
          <FiUsers size={16} className="chub-widget-icon rising" />
          <h3>Rising Communities</h3>
        </div>
        <div className="chub-widget-content">
          {loading ? (
            <WidgetSkeleton />
          ) : risingCommunities.length > 0 ? (
            <div className="chub-rising-list">
              {risingCommunities.slice(0, 3).map((community) => (
                <div
                  key={community._id}
                  className="chub-rising-item"
                  onClick={() =>
                    navigate(`/communities/${community._id}/preview`)
                  }
                >
                  <div className="chub-rising-avatar">
                    {community.signedPhotoURL || community.photoURL ? (
                      <img
                        src={community.signedPhotoURL || community.photoURL}
                        alt={community.name}
                      />
                    ) : (
                      <span>{community.name?.charAt(0).toUpperCase()}</span>
                    )}
                  </div>
                  <div className="chub-rising-info">
                    <span className="chub-rising-name">{community.name}</span>
                    <span className="chub-rising-meta">
                      {formatCount(community.memberCount)} members
                    </span>
                  </div>
                  {community.recentThreads > 0 && (
                    <span className="chub-rising-badge">
                      +{community.recentThreads}
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="chub-widget-empty">
              <FiUsers size={24} />
              <span>No rising communities</span>
            </div>
          )}
        </div>
      </motion.div>

      {/* Hot Discussions Widget */}
      <motion.div
        className="chub-widget"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <div className="chub-widget-header">
          <FiMessageSquare size={16} className="chub-widget-icon hot" />
          <h3>Hot Discussions</h3>
        </div>
        <div className="chub-widget-content">
          {loading ? (
            <WidgetSkeleton />
          ) : hotDiscussions.length > 0 ? (
            <div className="chub-discussions-list">
              {hotDiscussions.slice(0, 4).map((thread) => (
                <div
                  key={thread._id}
                  className="chub-discussion-item"
                  onClick={() =>
                    navigate(
                      `/communities/${thread.community?._id}/threads/${thread._id}`
                    )
                  }
                >
                  <div className="chub-discussion-content">
                    <span className="chub-discussion-title">{thread.title}</span>
                    <span className="chub-discussion-meta">
                      {thread.community?.name} • {formatTimeAgo(thread.lastActivity)}
                    </span>
                  </div>
                  <div className="chub-discussion-stats">
                    <span>
                      <FaComment size={10} /> {formatCount(thread.replyCount)}
                    </span>
                    <span className="upvote">
                      <FiArrowUp size={10} /> {formatCount(thread.upvoteCount)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="chub-widget-empty">
              <FiMessageSquare size={24} />
              <span>No hot discussions</span>
            </div>
          )}
        </div>
      </motion.div>

      {/* Saved Threads Quick Link */}
      <motion.div
        className="chub-widget chub-saved-widget"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.45 }}
      >
        <Link to="/communities?tab=saved" className="chub-saved-link">
          <div className="chub-saved-link-content">
            <div className="chub-saved-icon">
              <FiBookmark size={18} />
            </div>
            <div className="chub-saved-info">
              <span className="chub-saved-title">Saved Threads</span>
              <span className="chub-saved-count">
                {savedData?.count || 0} saved
              </span>
            </div>
          </div>
          <FiChevronRight size={16} className="chub-saved-arrow" />
        </Link>
      </motion.div>

      {/* Create Community Button */}
      <motion.button
        className="chub-create-btn"
        onClick={onCreateCommunity}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <FiPlus size={16} />
        <span>Create Community</span>
      </motion.button>
    </div>
  );
};

export default SidebarWidgets;
